import dayjs from "dayjs";
import {
  deriveSubscriptionState,
  SUBSCRIPTION_CARD_STATE,
} from "@components/PayRangeSubscriptionCard/payRangeSubscriptionCardUtils";
import { SUBSCRIPTION_BAR_STATE } from "@components/PayRangeSubscriptionBar/payRangeSubscriptionBarUtils";

const LEGEND_ORDER = [
  SUBSCRIPTION_BAR_STATE.LAPSED,
  SUBSCRIPTION_BAR_STATE.CURRENT,
  SUBSCRIPTION_BAR_STATE.FUTURE,
];

const LEGEND_LABEL = {
  [SUBSCRIPTION_BAR_STATE.LAPSED]: "Lapsed",
  [SUBSCRIPTION_BAR_STATE.CURRENT]: "Current",
  [SUBSCRIPTION_BAR_STATE.FUTURE]: "Future",
};

export function toTimelineBarState(cardState) {
  if (
    cardState === SUBSCRIPTION_CARD_STATE.CURRENT_OPEN ||
    cardState === SUBSCRIPTION_CARD_STATE.CURRENT_CLOSED ||
    cardState === SUBSCRIPTION_BAR_STATE.CURRENT
  ) {
    return SUBSCRIPTION_BAR_STATE.CURRENT;
  }
  if (
    cardState === SUBSCRIPTION_CARD_STATE.FUTURE_OPEN ||
    cardState === SUBSCRIPTION_CARD_STATE.FUTURE_CLOSED ||
    cardState === SUBSCRIPTION_BAR_STATE.FUTURE
  ) {
    return SUBSCRIPTION_BAR_STATE.FUTURE;
  }
  return SUBSCRIPTION_BAR_STATE.LAPSED;
}

export function collectWindows(subscriptions = [], assignments = []) {
  const windows = [];
  subscriptions.forEach((sub) => {
    windows.push({ start: sub.start, end: sub.end });
    (sub.versions || []).forEach((version) => {
      windows.push({
        start: version.start || version.effectiveWindow?.start,
        end: version.end || version.effectiveWindow?.end,
      });
    });
    (sub.coverageGaps || []).forEach((gap) => {
      windows.push({ start: gap.from, end: gap.to });
    });
  });
  assignments.forEach((assignment) => {
    windows.push({ start: assignment.start, end: assignment.end });
  });
  return windows;
}

function windowsAllEndedBefore(windows, now) {
  return (
    windows.length > 0 &&
    windows.every((window) => {
      if (!window.end) return false;
      const end = dayjs(window.end);
      return end.isValid() && end.isBefore(now, "day");
    })
  );
}

export function buildAxisRange(windows = [], today) {
  const now = dayjs(today);
  const yearCap = now.endOf("year");
  // Figma's all-past variants keep the axis on the data years (2023–2024)
  // and park "today" on the right — stretching to the real today date
  // would add 2025/2026 ticks and pin the marker at 100% (clipped).
  const allPast = windowsAllEndedBefore(windows, now);
  const dates = allPast ? [] : [now];
  windows.forEach((window) => {
    const start = dayjs(window.start);
    const end = dayjs(window.end);
    if (start.isValid()) dates.push(start);
    if (!window.end) {
      dates.push(yearCap);
      return;
    }
    if (!end.isValid()) return;
    // Figma clips a far-future subscription to a short stub after today
    // (3+ Future: Feb 2027–Jan 2029 draws only through mid-2027).
    if (start.isValid() && start.isAfter(now, "day")) {
      const stubEnd = start.add(5, "month");
      dates.push(end.isAfter(stubEnd) ? stubEnd : end);
    } else {
      dates.push(end);
    }
  });
  const valid = dates.filter((d) => d.isValid());
  const min = valid.reduce((a, b) => (a.isBefore(b) ? a : b)).startOf("year");
  let max = valid.reduce((a, b) => (a.isAfter(b) ? a : b));
  const lastDataEnd = max;
  if (max.isSame(min, "year") || !max.isAfter(min)) {
    max = min.add(1, "year").endOf("year");
  }
  if (allPast && now.isAfter(lastDataEnd, "day")) {
    const span = lastDataEnd.valueOf() - min.valueOf();
    const yearEnd = lastDataEnd.endOf("year");
    if (span > 0) {
      const padded = dayjs(min.valueOf() + span / 0.8);
      max = padded.isAfter(yearEnd) ? yearEnd : padded;
    }
  }
  return { start: min, end: max, today: now, lastDataEnd, allPast };
}

function pct(value, axis) {
  const t = dayjs(value).valueOf();
  const a = axis.start.valueOf();
  const b = axis.end.valueOf();
  if (b === a) return 0;
  return Math.max(0, Math.min(100, ((t - a) / (b - a)) * 100));
}

export function layoutWindow(start, end, axis) {
  const left = pct(start || axis.start, axis);
  const right = pct(end || axis.end, axis);
  return {
    left,
    width: Math.max(right - left, 1.5),
  };
}

function isBlank(value) {
  return value == null || value === "";
}

/**
 * Clip a child (version) window to its parent (subscription) window.
 * Null/undefined/"" = open-ended on that side. effective_to is inclusive
 * (MDP-614 P3), so a date equal to a boundary still counts as overlapping.
 * Returns null when the child falls entirely outside the parent (draw nothing).
 *
 * Versions only. Assignment (Occupied by) bars are deliberately NOT clipped —
 * an employee can occupy a position outside a pay range subscription.
 */
export function clipToParent(childStart, childEnd, parentStart, parentEnd) {
  const cs = isBlank(childStart) ? null : dayjs(childStart);
  const ce = isBlank(childEnd) ? null : dayjs(childEnd);
  const ps = isBlank(parentStart) ? null : dayjs(parentStart);
  const pe = isBlank(parentEnd) ? null : dayjs(parentEnd);

  // Entirely outside the parent (inclusive ends: equal dates still overlap).
  if (ps && ce && ce.isBefore(ps, "day")) return null;
  if (pe && cs && cs.isAfter(pe, "day")) return null;

  // Start: never earlier than the parent's start. Open-ended child inherits it.
  let start = isBlank(childStart) ? parentStart : childStart;
  if (ps && cs && cs.isBefore(ps, "day")) start = parentStart;

  // End: never later than the parent's end. Child open-ended + parent closed
  // => cap to the parent's end; parent open-ended => inherit the child's end.
  let end = isBlank(childEnd) ? null : childEnd;
  if (pe) {
    if (!ce) end = parentEnd;
    else if (ce.isAfter(pe, "day")) end = parentEnd;
  }

  return { start, end };
}

/** Version window clipped to its subscription (ER-11710). Null = dropped. */
export function clipVersionWindow(version = {}, sub = {}) {
  const childStart = version.start || version.effectiveWindow?.start;
  const childEnd = version.end ?? version.effectiveWindow?.end;
  return clipToParent(childStart, childEnd, sub.start, sub.end);
}

export const VERSION_BAR_MIN_WIDTH_PCT = 1.5;

/**
 * Lay out a clipped version window so its bar never crosses the parent
 * subscription's right edge, even when layoutWindow's min-width floor would
 * push it over (ER-11710). If the floor would overflow, shift left instead.
 */
export function layoutVersionWindow(childStart, childEnd, parentStart, parentEnd, axis) {
  const box = layoutWindow(childStart, childEnd, axis);
  const subBox = layoutWindow(parentStart, parentEnd, axis);
  const subRight = subBox.left + subBox.width;
  const maxWidth = Math.max(subRight - box.left, 0);

  let { left, width } = box;
  if (width > maxWidth) {
    width = maxWidth;
    if (width < VERSION_BAR_MIN_WIDTH_PCT) {
      left = Math.max(subBox.left, subRight - VERSION_BAR_MIN_WIDTH_PCT);
      width = VERSION_BAR_MIN_WIDTH_PCT;
    }
  }
  return { left, width };
}

const GAP_LABEL = "no pay range in force";
const GAP_LABEL_MIN_WIDTH_PCT = 14;

export function gapLabelOverflowsSpan(boxWidthPct) {
  return boxWidthPct < GAP_LABEL_MIN_WIDTH_PCT;
}

export function gapBarLabelText() {
  return GAP_LABEL;
}

export function yearLabels(axis) {
  const labels = [];
  let cursor = axis.start.startOf("year");
  while (!cursor.isAfter(axis.end, "year")) {
    labels.push({
      year: String(cursor.year()),
      left: pct(cursor, axis),
    });
    cursor = cursor.add(1, "year");
    if (labels.length > 12) break;
  }
  return labels;
}

/** Keep the marker off 0%/100% so the "today" label is not clipped. */
const TODAY_AXIS_INSET_PCT = 7;
/** Figma Past variants park "today" just after the last bar, inset from the right. */
const PAST_TODAY_MIN_PCT = 88;
const PAST_TODAY_MAX_PCT = 96;

export function todayLeft(axis) {
  if (axis.allPast) {
    const barEnd = pct(axis.lastDataEnd || axis.end, axis);
    return Math.max(PAST_TODAY_MIN_PCT, Math.min(PAST_TODAY_MAX_PCT, barEnd + 5));
  }
  const raw = pct(axis.today, axis);
  return Math.max(TODAY_AXIS_INSET_PCT, Math.min(100 - TODAY_AXIS_INSET_PCT, raw));
}

export function legendStates(subscriptions = [], today) {
  const present = new Set(
    subscriptions.map((sub) =>
      toTimelineBarState(deriveSubscriptionState(sub.start, sub.end, today))
    )
  );
  return LEGEND_ORDER.filter((state) => present.has(state)).map((state) => ({
    state,
    label: LEGEND_LABEL[state],
  }));
}

export function sortSubscriptionsNewestFirst(subscriptions = []) {
  return [...subscriptions].sort((a, b) => {
    const aStart = dayjs(a.start).valueOf() || 0;
    const bStart = dayjs(b.start).valueOf() || 0;
    return bStart - aStart;
  });
}

export function formatStatusLabel(status) {
  const value = String(status || "active").trim();
  if (!value) return "Active";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

export function formatCodeSubtitle(code) {
  const value = String(code || "").trim();
  return value ? `${value} \u00B7 Fair pay` : "Fair pay";
}

export { LEGEND_LABEL };
